import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React, { useEffect, useState, useContext } from "react";
import { TruncateCopy } from "views/admin/organizations/components/CreateOrgModal";
import { ClientRowObj } from "..";
import ArrangeClientModal from "./ArrangeClientModal";
import CreateClientGatewaysModal from "./CreateClientGatewaysModal";
import CheckoutProductClientModal from "./CheckoutProductClientModal";
import CreateClientModal from "./CreateClientModal";
import ClientDetails from "./ClientDetails";
import CustomerRegistration from "./CustomerRegistration";
import DeleteClientModal from "./DeleteClientModal";
import AddTreasury from "./AddTreasury";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import Searchbox from "components/fields/Searchbox";
import Pagination from "components/pagination";
import CustomToolTip from "components/tooltips/new";
import { ClientContext } from "ClientProvider";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";
import { MdInfo } from "react-icons/md";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<ClientRowObj>();

function ClientsTable(props: {
  tableData: any;
  fetchClients: () => void;
  isLoading: boolean;
  roleData?: any;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  allGateways: any;
  onValueChange: (value: string) => void;
}) {
  const {
    tableData, fetchClients, roleData, page, setPage, currentPage,
    totalpage, totalItems, pageSize, setPageSize, allGateways, onValueChange, isLoading,
  } = props;

  const { singleRoleData } = useContext(ClientContext);

  const getIdByName = (name: any) => {
    const found = allGateways.find((item: any) => item.name === name);
    return found ? found.id : name;
  };

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  const [searchVal, setSearchVal] = useState<any>("");

  useEffect(() => { setData(tableData ?? []); }, [tableData, searchVal]);

  const handleValueChange = (e: any) => { onValueChange(e); setSearchVal(e); };

  const GatewayCell = (info: any) => {
    const gatewayObj = info.getValue() || {};
    const gatewayList = Object.keys(gatewayObj).filter((k) => gatewayObj[k]);

    return (
      <CustomToolTip
        header="Enabled Gateways"
        content={
          gatewayList.length ? (
            <div className="ct-gw-tooltip-grid">
              {gatewayList.map((m: any) => (
                <span key={m} className="ct-gw-chip">
                  {
                    singleRoleData[0]?.client?.value
                      ?.client_show_method_name
                      ? m
                      : getIdByName(m)
                  }
                </span>
              ))}
            </div>
          ) : (
            <span className="ct-no-gw">No gateways assigned</span>
          )
        }
        trigger={
          <div
            className={`ct-gw-pill ${
              gatewayList.length === 0 ? "ct-gw-pill--empty" : ""
            }`}
          >
            <span className="ct-gw-dot" />
            <span>{gatewayList.length}</span>

            {gatewayList.length > 0 && (
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
                fill="none"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </div>
        }
      />
    );
  };

  const columns = [
    columnHelper.accessor("org_name", {
      id: "org_name",
      header: () => <span className="ct-th">Organization</span>,
      cell: (info: any) => (
        <div className="ct-org-cell">
          <div className="ct-org-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "O"}</div>
          <span className="ct-org-name">{info.getValue()}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.org_name || "";
        const b = rowB.original.org_name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.client_id).localeCompare(String(rowB.original.client_id));
      },
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: () => <span className="ct-th">Client Name</span>,
      cell: (info: any) => (
        <div className="ct-name-cell">
          <div className="ct-name-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "C"}</div>
          <span className="ct-name">{info.getValue()}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.name || "";
        const b = rowB.original.name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.client_id).localeCompare(String(rowB.original.client_id));
      },
    }),
    columnHelper.accessor("client_id", {
      id: "client_id",
      header: () => <span className="ct-th">Client ID</span>,
      cell: (info: any) => <div className="ct-mono-cell"><ShortTruncateCopy info={info?.getValue()} showCopy={true} /></div>,
      enableSorting: true,
    }),
    columnHelper.accessor("client_secret", {
      id: "client_secret",
      header: () => <span className="ct-th">Client Secret</span>,
      cell: (info: any) => <div className="ct-mono-cell"><ShortTruncateCopy info={info?.getValue()} showCopy={true} /></div>,
    }),
    columnHelper.accessor("client_gateway_active", {
      id: "client_gateway_active",
      header: () => <span className="ct-th">Gateways</span>,
      cell: GatewayCell,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId) as Record<string, boolean>;
        const b = rowB.getValue(columnId) as Record<string, boolean>;
        return Object.keys(a || {}).filter((k) => a[k]).length - Object.keys(b || {}).filter((k) => b[k]).length;
      },
      enableSorting: true,
    }),
    columnHelper.accessor("assigned_payment_methods", {
      id: "assigned_payment_methods",
      header: () => <span className="ct-th">Actions</span>,
      cell: (info) => {
        const paymentmethods = info?.cell?.row?.original.assigned_payment_methods;
        const methodObj = { assigned_payment_methods: paymentmethods };
        return (
          <div className="ct-actions">
            {(roleData?.[0]?.client?.value?.client_gateway_settings ||
              roleData?.[0]?.client?.value?.customer_registration || 
              roleData?.[0]?.client?.value?.client_checkout_setting || 
              roleData?.[0]?.client?.value?.client_gateway_routing || 
              roleData?.[0]?.client?.value?.view_client || 
              roleData?.[0]?.client?.value?.view_client_treasury)  && (
                <Popover trigger="hover" placement="bottom">
                  <PopoverTrigger>
                  <div>
                    <Link className="inline-flex" to={`/admin/client-details/${info?.cell?.row?.original.client_id}`} >
                      <div className="ccm-action-icon ccm-action-icon--blue" >
                      <MdInfo size={16} />
                      </div>
                    </Link>
                  </div>
                  </PopoverTrigger>
                  <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" boxShadow="0 8px 24px rgba(15,23,42,0.08)" bg="#eff6ff" maxW="260px" p="0" overflow="hidden" zIndex="20">
                    <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="14px" py="10px">View Client Details</PopoverHeader>
                    <PopoverArrow bg="#eff6ff" />
                  </PopoverContent>
              </Popover>
            )}
            <CreateClientModal fetchClients={fetchClients} id={info?.cell?.row?.original.client_id} roleData={roleData} allGateways={allGateways} infoData={info} />
            {/* <CreateClientModal fetchClients={fetchClients} id={info?.cell?.row?.original.client_id} info={true} roleData={roleData} allGateways={allGateways} infoData={info} /> */}
            {roleData?.[0]?.client?.value?.delete_client && (
              <DeleteClientModal fetchClients={fetchClients} id={info?.cell?.row?.original.client_id} />
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
  ];

  const columnsNonAction = columns.slice(0, 5);

  const hasActionPerms =
    roleData?.[0]?.client?.value?.client_checkout_setting ||
    roleData?.[0]?.client?.value?.client_gateway_settings ||
    roleData?.[0]?.client?.value?.client_gateway_routing ||
    roleData?.[0]?.client?.value?.delete_client ||
    roleData?.[0]?.client?.value?.edit_client ||
    roleData?.[0]?.client?.value?.view_client_treasury ||
    roleData?.[0]?.client?.value?.customer_registration ||
    roleData?.[0]?.client?.value?.view_client;

  const table = useReactTable({
    data,
    columns: hasActionPerms ? columns : columnsNonAction,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="ct-card">
      {/* Header */}
      <div className="ct-header">
        <div className="ct-header-left">
          <div className="ct-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h3 className="ct-title">All Clients</h3>
            <p className="ct-subtitle">{totalItems > 0 ? `${totalItems} total clients` : "No clients found"}</p>
          </div>
        </div>
        <div className="ct-search-wrap">
          <CreateClientModal fetchClients={fetchClients} roleData={singleRoleData} allGateways={allGateways} />
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="ct-table-wrap">
        {isLoading ? (
          <div className="ct-loading">
            <div className="ct-spinner" />
            <p className="ct-loading-txt">Loading clients…</p>
          </div>
        ) : (
          <table className="ct-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan} onClick={h.column.getToggleSortingHandler()} className="ct-thead-th">
                      <div className="ct-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="ct-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr key={row.id} className={`ct-tbody-tr${ri % 2 !== 0 ? " ct-tbody-tr--alt" : ""}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="ct-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="ct-empty">
                      <div className="ct-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </div>
                      <p className="ct-empty-title">No clients found</p>
                      <p className="ct-empty-sub">Try adjusting your search or add a new client</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="ct-pagination-wrap">
        <Pagination
          setPage={setPage} page={page} totalpage={totalpage}
          currentPage={currentPage} pageSize={pageSize}
          setPageSize={setPageSize} arraySize={[50, 100, 200]}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        
        .ct-gw-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 9px;
          font-size: 12px;
          font-weight: 700;

          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          border: 1.5px solid #bfdbfe;
          color: #1d4ed8;

          transition: 0.15s ease;
          cursor: pointer;
        }

        .ct-gw-pill:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .ct-gw-pill--empty {
          background: #f8fafc;
          border-color: #e2e8f0;
          color: #94a3b8;
          cursor: default;
        }

        /* status dot inside gateway badge */

        .ct-gw-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #2563eb;
        }

        /* tooltip gateway chips */

        .ct-gw-tooltip-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .ct-gw-chip {
          font-size: 11.5px;
          font-weight: 600;

          padding: 4px 8px;
          border-radius: 6px;

          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #374151;
        }
        .ct-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .ct-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
        }
        .ct-header-left { display: flex; align-items: center; gap: 12px; }
        .ct-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .ct-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px;line-height:22px }
        .ct-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .ct-search-wrap { display: flex; gap: 10px;min-width: 220px; }

        /* Table */
        .ct-table-wrap { overflow-x: auto; }
        .ct-table { width: 100%; border-collapse: collapse; }

        .ct-thead-th {
          padding: 12px 16px 10px;
          background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; cursor: pointer; white-space: nowrap; user-select: none;
        }
        .ct-thead-th:hover { background: #f1f5f9; }
        .ct-th-inner { display: flex; align-items: center; gap: 5px; }
        .ct-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
        .ct-sort-icon { color: #94a3b8; display: flex; align-items: center; transition: color 0.15s; }
        .ct-thead-th:hover .ct-sort-icon { color: #2563eb; }

        .ct-tbody-tr { transition: background 0.12s; }
        .ct-tbody-tr:hover { background: #f8fafc; }
        .ct-tbody-tr--alt { background: #fafbfc; }
        .ct-tbody-tr--alt:hover { background: #f1f5f9; }
        .ct-tbody-td { padding: 13px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Cells */
        .ct-org-cell, .ct-name-cell { display: flex; align-items: center; gap: 9px; }
        .ct-org-avatar, .ct-name-avatar {
          width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700;
        }
        .ct-org-avatar { background: linear-gradient(135deg, #f0fdf4, #dcfce7); border: 1.5px solid #bbf7d0; color: #15803d; }
        .ct-name-avatar { background: linear-gradient(135deg, #eff6ff, #dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .ct-org-name, .ct-name { font-size: 13px; font-weight: 600; color: #0f172a; }
        .ct-mono-cell { font-size: 12.5px;font-weight:bold }

        /* Gateway count badge */
        .ct-gw-count {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 12.5px; font-weight: 700;
          padding: 5px 11px; border-radius: 8px; cursor: pointer;
          background: #eff6ff; color: #1d4ed8; border: 1.5px solid #bfdbfe;
          transition: all 0.15s;
        }
        .ct-gw-count:hover { background: #dbeafe; border-color: #93c5fd; }
        .ct-gw-count--zero { background: #f8fafc; color: #94a3b8; border-color: #e2e8f0; cursor: default; }
        .ct-gw-tag {
          background: #f1f5f9; color: #374151; padding: 3px 8px;
          border-radius: 6px; font-size: 11.5px; font-weight: 600;
        }
        .ct-no-gw { font-size: 12px; color: #94a3b8; font-style: italic; }

        /* Actions */
        .ct-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

        /* Empty */
        .ct-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 24px; gap: 10px; }
        .ct-empty-icon { width: 52px; height: 52px; border-radius: 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .ct-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .ct-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .ct-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px; gap: 12px; }
        .ct-spinner { width: 28px; height: 28px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: ctSpin 0.7s linear infinite; }
        @keyframes ctSpin { to { transform: rotate(360deg); } }
        .ct-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .ct-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .ct-header { flex-direction: column; align-items: flex-start; }
          .ct-search-wrap { width: 100%; }
          .ct-thead-th, .ct-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default ClientsTable;